import type { Status } from '../../content';
import type { Sector } from './sectors';

export type Entry = { date: string; title: string; meta: string };
export type Chapter = {
  key: string;
  stage: string; // short label for the stepper
  place: (s: Sector) => string;
  title: (s: Sector) => [string, string, string]; // before, swipe, after
  body: (s: Sector) => string;
  gain: (s: Sector) => string;
  also?: (s: Sector) => { t: string; status?: Status }; // a folded-in moment, one line
  status: Status;
  entries: (s: Sector) => Entry[];
};

const low = (t: string) => (/^[A-Z][a-z]/.test(t) ? t.charAt(0).toLowerCase() + t.slice(1) : t);
// Mid-sentence: 'The owner' reads as 'the owner'; names stay as they are.
const mid = (t: string) => (t.startsWith('The ') ? 't' + t.slice(1) : t);
const yearOf = (d: string) => d.split(' ').pop() ?? d;

export const CHAPTERS: Chapter[] = [
  {
    key: 'built',
    stage: 'Built',
    place: () => 'At the factory',
    title: () => ['It starts in ', 'your ERP', '.'],
    body: (s) =>
      `Works order ${s.wo} closes. The serial comes from your ERP, the code goes on the plate, and the ${s.noun} has a record before it leaves the line.`,
    gain: () => 'Your ERP stays the master. Nobody types anything twice.',
    also: (s) => ({ t: `The packing slip carries the same code and the ${low(s.cert)}.` }),
    status: 'live',
    entries: (s) => [
      { date: s.dates.built, title: 'Serial created', meta: `From works order ${s.wo}` },
      { date: s.dates.built, title: 'Plate marked', meta: `${s.maker} code on the serial plate` },
      { date: s.dates.shipped, title: `Shipped to ${s.partner}`, meta: `${s.slip}, certificate attached` },
    ],
  },
  {
    key: 'handover',
    stage: 'Handover',
    place: (s) => s.handoverPlace,
    title: () => ['The warranty starts at ', 'handover', '.'],
    body: (s) =>
      `The ${s.partnerRole} ticks off the ${low(s.handoverName)} on a phone and ${mid(s.owner1Short)} signs on the same screen. Cover starts today, ${s.partner} is credited, and you know who has it.`,
    gain: (s) => `The ${s.partnerRole} needs no app and stays on the record.`,
    status: 'pilot',
    entries: (s) => [{ date: s.dates.handover, title: `${s.handoverName} signed`, meta: `${s.partnerShort} and owner, warranty started` }],
  },
  {
    key: 'owned',
    stage: 'Owned',
    place: (s) => s.where,
    title: () => ['The owner gets ', 'that unit', '’s own page.'],
    body: (s) =>
      `${s.owner1Short} scans the plate, with no app or password, and gets this serial's own page in your brand: the right manual, the warranty, and service with ${s.partnerShort}.`,
    gain: () => 'You learn who owns it.',
    status: 'live',
    entries: (s) => [{ date: s.dates.owned, title: 'Owner registered', meta: 'Two taps, consent captured' }],
  },
  {
    key: 'asked',
    stage: 'Asked',
    place: (s) => s.ask.when,
    title: () => ['The answer comes from ', 'your manual', '.'],
    body: (s) =>
      `${s.owner1Short} asks the ${s.noun} what is wrong and gets an answer for this serial, with the manual page it came from.`,
    gain: (s) => `One tap reaches a person at ${s.partnerShort} or on your team.`,
    status: 'live',
    entries: (s) => [{ date: s.dates.asked, title: 'Question answered', meta: `${s.ask.src.split(' · ')[0]}, cited` }],
  },
  {
    key: 'claim',
    stage: 'Claimed',
    place: (s) => `With ${s.partner}`,
    title: () => ['The claim arrives ', 'complete', '.'],
    body: (s) =>
      `${s.claim.fault}. ${s.partnerShort} files the claim from the record, so it reaches you with the serial, photos, ${low(s.handoverName)} and the owner's conversation.`,
    gain: () => 'Proof of purchase is already there. Returns flow back to your ERP.',
    status: 'live',
    entries: (s) => [{ date: s.dates.claim, title: `Claim ${s.claim.id} approved`, meta: `Arrived complete, ${s.partnerShort} credited` }],
  },
  {
    key: 'serviced',
    stage: 'Serviced',
    place: (s) => `At the ${s.service.name}`,
    title: () => ['The service is booked ', 'before it is due', '.'],
    body: (s) =>
      `The page tells ${mid(s.owner1Short)} the ${s.service.name} is due and books it with ${s.partnerShort}. The engineer scans the plate and arrives with the ${s.service.kit}.`,
    gain: () => 'Fewer breakdowns, fewer claims, and every part sold by you or your dealer.',
    also: (s) => ({ t: `Bulletins reach the serials they are for, on the page ${s.operator} already scans.` }),
    status: 'live',
    entries: (s) => [
      { date: s.dates.bulletin, title: `${s.bulletin.code} delivered`, meta: s.bulletin.action },
      { date: s.service.date, title: `${s.service.name.charAt(0).toUpperCase()}${s.service.name.slice(1)} done`, meta: `${s.partnerShort}, ${s.service.kit} fitted` },
    ],
  },
  {
    key: 'sold',
    stage: 'Sold on',
    place: (s) => `${yearOf(s.dates.sold)}, years later`,
    title: () => ['Sold on, with its ', 'history', '.'],
    body: (s) =>
      `${s.transferNote} The owner agrees, ${s.owner2} takes over the record, and the previous owner's details stay private.`,
    gain: () => 'You keep a line to every owner.',
    status: 'live',
    entries: (s) => [{ date: s.dates.sold, title: 'Ownership transferred', meta: `To ${s.owner2}, with consent` }],
  },
  {
    key: 'recycle',
    stage: 'Recycle',
    place: (s) => `${s.dates.recycled}, end of life`,
    title: () => ['Its ', 'next chapter', '.'],
    body: (s) =>
      `When the ${s.noun} is done, the same scan shows the take-back route through ${s.takeBack} and the materials data recyclers need.`,
    gain: () => 'Take-back and refurbishment start from the record.',
    also: () => ({ t: 'Where the rules require a Digital Product Passport, the same code can carry it, from data you already hold.' }),
    status: 'pilot',
    entries: (s) => [
      { date: s.dates.recycled, title: 'Taken back', meta: `${s.partnerShort}, materials data shared` },
      { date: s.dates.recycled, title: 'Recycler view opened', meta: 'Same code, materials data' },
    ],
  },
];

export const chapterIndex = (key: string) => CHAPTERS.findIndex((c) => c.key === key);

export type Fields = { stage: string; owner: string; partner: string; warranty: string };

export function fieldsAt(s: Sector, i: number): Fields {
  const stage = ['Built', 'Installed', 'In use', 'In use', 'In repair', 'Serviced', 'In use, 2nd owner', 'Taken back'][i] ?? 'Built';
  const owner = i < 1 ? 'Not known yet' : i < 6 ? s.owner1 : s.owner2;
  const partner = s.partner;
  const warranty = i < 1 ? 'Not started' : i < 6 ? `From ${s.dates.handover}` : i < 7 ? 'Carried over' : 'Ended';
  return { stage, owner, partner, warranty };
}

export function entriesUpTo(s: Sector, i: number): Entry[] {
  return CHAPTERS.slice(0, i + 1).flatMap((c) => c.entries(s));
}
